import {
  Smartphone, Bot, LayoutDashboard,
  Rocket, Package, Zap,
  Radio, Mic, Cog, Plane,
  ScanSearch, Truck, LineChart,
} from "lucide-react";
import PillarPage from "./PillarPage";

/**
 * Centralised definitions for the four new WarehouseX pillar pages
 * (SmartExecution, SmartAcceleration, SmartWarehouse Tech, SmartIntegrations).
 *
 * Hero images reuse the SAME URLs already used by the corresponding pillar
 * blocks on the WarehouseX landing page (`pages/PlatformHome.jsx`) so the
 * cross-page visual language stays identical without touching that file.
 */

// Image URLs match PlatformHome's `SmartExecutionVisual` / `SmartAccelerationVisual` /
// `SmartTechVisual` / `SmartIntegrationsVisual` — single source of truth.
const IMG_EXECUTION =
  "/images/smartexectution.webp";
const IMG_ACCELERATION =
  "/images/smartaccelaration.webp";
const IMG_TECH =
  "/images/smartwarehouse-tech.webp";
const IMG_INTEGRATIONS =
  "/images/smartintegrations.webp";

// -- SmartExecution ---------------------------------------------------------
export const SmartExecutionPage = () => (
  <PillarPage
    testIdPrefix="smartexecution"
    title="WarehouseX SmartExecution"
    description="Execute warehouse operations with modern RF interfaces, real-time visibility, and intelligent execution tools designed to improve productivity and operational accuracy."
    supportText="Choose a module below to optimize warehouse execution operations."
    bulletText="Three connected modules — SmartRF, SmartEWM Bot, and SmartCenter."
    heroImage={IMG_EXECUTION}
    heroAlt="SmartExecution — RF device, SmartCenter dashboard, and SmartBot AI assistant in a live warehouse"
    modules={[
      {
        id: "smartrf",
        title: "SmartRF (PreBilt)",
        description: "Modern RF execution layer designed to simplify warehouse operations and improve operator productivity.",
        icon: Smartphone,
        href: "/smartexecution/smartrf",
      },
      {
        id: "smartewm-bot",
        title: "SmartEWM Bot",
        description: "AI-powered warehouse support assistant for operational guidance, issue resolution, and warehouse execution support.",
        icon: Bot,
      },
      {
        id: "smartcenter",
        title: "SmartCenter",
        description: "Centralized warehouse monitoring and operational visibility platform for supervisors and operations teams.",
        icon: LayoutDashboard,
      },
    ]}
  />
);

// -- SmartAcceleration ------------------------------------------------------
export const SmartAccelerationPage = () => (
  <PillarPage
    testIdPrefix="smartacceleration"
    title="WarehouseX SmartAcceleration"
    description="Accelerate SAP warehouse implementations using prebuilt kits, reusable accelerators, and industry-driven deployment frameworks."
    supportText="Choose a module below to accelerate warehouse transformation delivery."
    bulletText="Three connected modules — Go-Live Rapid Kits, Industry Packs, and Solution Accelerators."
    heroImage={IMG_ACCELERATION}
    heroAlt="SmartAcceleration — Implementation Accelerator dashboard with Discover, Design, Build & Deploy, Go-Live phases"
    modules={[
      {
        id: "go-live-rapid-kits",
        title: "Go-Live Rapid Kits",
        description: "Prebuilt deployment kits designed to reduce implementation timelines and simplify warehouse go-live activities.",
        icon: Rocket,
      },
      {
        id: "industry-packs",
        title: "Industry Packs",
        description: "Industry-specific warehouse process templates and configurations built from real implementation experience.",
        icon: Package,
      },
      {
        id: "solution-accelerators",
        title: "Solution Accelerators",
        description: "Reusable accelerators and enhancements designed to speed up warehouse transformation projects.",
        icon: Zap,
      },
    ]}
  />
);

// -- SmartWarehouse Tech (4 modules → 2x2 grid) -----------------------------
export const SmartWarehouseTechPage = () => (
  <PillarPage
    testIdPrefix="smartwarehouse-tech"
    title="WarehouseX SmartWarehouse Tech"
    description="Enable next-generation warehouse operations using RFID, automation, and advanced warehouse technologies integrated with SAP EWM and TM."
    supportText="Choose a module below to explore next-generation warehouse capabilities."
    bulletText="Four connected modules — RFID Enablement, Voice Picking, Automation, and Drone Inventory."
    heroImage={IMG_TECH}
    heroAlt="SmartWarehouse Tech — operator with voice headset, RFID tracking, autonomous robot, robotic arm on conveyor, and drone scanning"
    modules={[
      {
        id: "rfid-enablement",
        title: "RFID Enablement",
        description: "Enable real-time inventory visibility and automated warehouse tracking using RFID-driven warehouse processes.",
        icon: Radio,
      },
      {
        id: "voice-picking",
        title: "Voice Picking",
        description: "Hands-free warehouse execution capabilities designed to improve picking productivity and operational efficiency.",
        icon: Mic,
      },
      {
        id: "automation",
        title: "Automation",
        description: "Integrate warehouse automation technologies and intelligent execution flows with SAP warehouse operations.",
        icon: Cog,
      },
      {
        id: "drone-inventory",
        title: "Drone Inventory",
        description: "Advanced warehouse inventory tracking and monitoring using next-generation drone-enabled inventory capabilities.",
        icon: Plane,
      },
    ]}
  />
);

// -- SmartIntegrations ------------------------------------------------------
export const SmartIntegrationsPage = () => (
  <PillarPage
    testIdPrefix="smartintegrations"
    title="WarehouseX SmartIntegrations"
    description="Extend SAP warehouse operations using connected ecosystems, inspection platforms, shipping integrations, and data-driven operational insights."
    supportText="Choose a module below to explore warehouse integrations."
    bulletText="Three connected modules — SmartInspect, Tcube, and Futuroot."
    heroImage={IMG_INTEGRATIONS}
    heroAlt="SmartIntegrations — connected ecosystem with inspection tablet, analytics dashboard, freight management, and process flow"
    modules={[
      {
        id: "smartinspect",
        title: "SmartInspect",
        description: "AI-powered logistics inspection and claims evidence platform for SAP with operational quality and compliance visibility.",
        icon: ScanSearch,
        href: "/smartintegrations/smartinspect",
      },
      {
        id: "tcube",
        title: "Tcube",
        description: "SAP-aligned intelligent solutions that simplify freight management, automate shipping, and bring full visibility to your supply chain.",
        icon: Truck,
      },
      {
        id: "futuroot",
        title: "Futuroot",
        description: "Data-driven operational insights and warehouse analytics platform for continuous warehouse improvement.",
        icon: LineChart,
      },
    ]}
  />
);
